// Plain loops instead of findLast, findLastIndex and at(): phones on Safari before
// 15.4 run this code too, and the build doesn't polyfill them
function lastIndexWhere(items, test) {
    for (let i = items.length - 1; i >= 0; i--)
        if (test(items[i]))
            return i;
    return -1;
}
const lastWhere = (items, test) => items[lastIndexWhere(items, test)];
const last = (items) => items[items.length - 1];
/** Event time, then id: the same order on every device and on the server. */
export const chronological = (a, b) => a.created_at.getTime() - b.created_at.getTime() ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const emptyPhase = (kind, operatore_id, repairId) => ({
    kind,
    operatore_id,
    startedAt: null,
    closedAt: null,
    esito: null,
    note: null,
    cancelled: false,
    correction: null,
    repairId,
    closingId: null,
});
/**
 * An intervento's history from its events: the inspection and each repair, with
 * their outcome after corrections. Pure and deterministic, so the server and every
 * phone fold the same events to the same result.
 */
export function foldEvents(events) {
    const ordered = [...events].sort(chronological);
    // The first closing of a phase closes it; a later one (two devices completing
    // offline) changes nothing
    const close = (phase, closing) => {
        if (phase.closingId !== null)
            return;
        phase.closedAt = closing.created_at;
        phase.closingId = closing.id;
        if (closing.tipo === 'riparazione_annullata') {
            phase.cancelled = true;
        }
        else {
            phase.esito = closing.esito;
            phase.note = closing.note;
        }
    };
    let inspection = null;
    for (const event of ordered) {
        if (event.tipo !== 'creato' &&
            event.tipo !== 'avviato' &&
            event.tipo !== 'completato')
            continue;
        inspection ?? (inspection = emptyPhase('inspection', event.operatore_id, null));
        if (event.tipo === 'avviato')
            inspection.startedAt = event.created_at;
        if (event.tipo === 'completato')
            close(inspection, event);
    }
    // Every repair started, in start order
    const repairs = [];
    const repairsById = new Map();
    for (const event of ordered) {
        if (event.tipo !== 'riparazione_avviata')
            continue;
        const phase = emptyPhase('repair', event.operatore_id, event.id);
        phase.startedAt = event.created_at;
        repairs.push(phase);
        repairsById.set(event.id, phase);
    }
    // Closings that name their repair first, by id, so a completion recorded on a
    // device whose clock runs behind still closes it; a name nobody has started (yet,
    // on this device) closes nothing. Then those without a name (none from the app)
    // take the latest repair still open, whatever its start time says
    const isOpen = (phase) => phase.closingId === null;
    const closings = ordered.filter((event) => event.tipo === 'riparazione_completata' ||
        event.tipo === 'riparazione_annullata');
    for (const event of closings) {
        if (!event.riparazione_id)
            continue;
        const phase = repairsById.get(event.riparazione_id);
        if (phase)
            close(phase, event);
    }
    for (const event of closings) {
        if (event.riparazione_id)
            continue;
        const phase = lastWhere(repairs, isOpen);
        if (phase)
            close(phase, event);
    }
    // A closed phase's outcome: the latest correction of it that sets each field
    // wins. A correction names the repair it changes, or the inspection with none;
    // one of a phase still open, or of a cancelled repair, waits or is ignored
    const phases = inspection ? [inspection, ...repairs] : repairs;
    for (const phase of phases) {
        if (phase.closingId === null || phase.cancelled)
            continue;
        const applied = ordered.filter((event) => event.tipo === 'correzione' && event.riparazione_id === phase.repairId);
        phase.esito =
            lastWhere(applied, (c) => c.esito !== null)?.esito ?? phase.esito;
        phase.note = lastWhere(applied, (c) => c.note !== null)?.note ?? phase.note;
        const latest = last(applied);
        phase.correction = latest
            ? { created_at: latest.created_at, operatore_id: latest.operatore_id }
            : null;
    }
    // The latest repair closed, by closing time; a repair's outcome supersedes the
    // inspection's, whatever the clocks said
    const closedRepairs = repairs
        .filter((phase) => phase.closingId && !phase.cancelled)
        .sort((a, b) => a.closedAt.getTime() - b.closedAt.getTime() ||
        (a.closingId < b.closingId ? -1 : 1));
    const latestClosed = last(closedRepairs) ?? (inspection?.closingId ? inspection : null);
    return {
        phases,
        openRepair: last(repairs.filter(isOpen)) ?? null,
        currentEsito: latestClosed?.esito ?? null,
        attempts: repairs.filter((phase) => !phase.cancelled).length,
    };
}
/**
 * Status, esito and current operatore from the events. Status only moves forward
 * (bozza, in corso, completato) whatever order the device clocks put the events in.
 */
export function projectEvents(events) {
    const types = new Set(events.map((event) => event.tipo));
    const status = types.has('completato')
        ? 'completato'
        : types.has('avviato')
            ? 'in_corso'
            : types.has('creato')
                ? 'bozza'
                : null;
    const { phases, currentEsito } = foldEvents(events);
    const repairer = lastWhere(phases, (phase) => phase.kind === 'repair' && !phase.cancelled);
    return {
        status,
        esito: currentEsito,
        current_operatore_id: repairer?.operatore_id ?? null,
    };
}
/**
 * The projected columns to write on a row, its own values where the events don't
 * decide: the status of a row without lifecycle events (no operatore to record
 * them as), and the esito before completion, which the inspector may note down
 * while working and the completion confirms.
 */
export function projectRow(events, row) {
    const projection = projectEvents(events);
    return {
        status: projection.status ?? row.status,
        esito: projection.status === 'completato' ? projection.esito : row.esito,
        current_operatore_id: projection.current_operatore_id,
    };
}
